import { definePersistenceEntry, type ModulePersistenceEntry } from '@krp/client-runtime'

import { priceTagsRestored } from './priceTagsSlice'
import { MAX_LABEL_COPIES, templateOf } from './templates'
import type { PriceTagItem, PriceTagSelection, PriceTagsState } from './types'

/**
 * Подбор ценников переживает перезагрузку (срок `device`, по пользователю):
 * это намерение человека — что он набрал сканером. Ответы службы не
 * сохраняются; цена в позиции — снимок момента скана, и экран показывает её
 * возраст. Через неделю брошенный подбор не нужен.
 */
const WEEK_MS = 7 * 24 * 60 * 60 * 1000

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

function decodeItem(raw: unknown): PriceTagItem | undefined {
  if (!isRecord(raw)) return undefined
  const { productRefKey, name, barcode, price, copies, pricedAt } = raw
  if (typeof productRefKey !== 'string' || productRefKey === '') return undefined
  if (typeof name !== 'string' || typeof barcode !== 'string') return undefined
  if (pricedAt !== null && typeof pricedAt !== 'string') return undefined
  if (price !== null && (typeof price !== 'number' || !Number.isFinite(price))) return undefined
  if (typeof copies !== 'number' || !Number.isInteger(copies) || copies < 1 || copies > MAX_LABEL_COPIES) return undefined
  // Ключи — в одном написании с кэшем и сканом: иначе повторный скан задвоил бы позицию.
  return { productRefKey: productRefKey.toLowerCase(), name, barcode, price, copies, pricedAt }
}

function decodeSelection(raw: unknown): PriceTagSelection | undefined {
  if (!isRecord(raw)) return undefined
  const { warehouseRefKey, warehouseName, templateId, items } = raw
  if (typeof warehouseRefKey !== 'string' || typeof warehouseName !== 'string' || typeof templateId !== 'string') return undefined
  if (!Array.isArray(items)) return undefined
  const decoded = items.map(decodeItem)
  if (decoded.some((item) => item === undefined)) return undefined
  return {
    warehouseRefKey: warehouseRefKey.trim().toLowerCase(),
    warehouseName,
    templateId: templateOf(templateId).id,
    items: decoded as PriceTagItem[],
  }
}

export function decodePriceTags(raw: unknown): PriceTagsState['byResource'] | undefined {
  if (!isRecord(raw)) return undefined
  const result: PriceTagsState['byResource'] = {}
  for (const [rid, value] of Object.entries(raw)) {
    const selection = decodeSelection(value)
    if (selection === undefined) return undefined
    result[rid.trim().toLowerCase()] = selection
  }
  return result
}

export function priceTagsPersistence<S>(select: (state: S) => PriceTagsState): ModulePersistenceEntry<S> {
  return definePersistenceEntry<S, PriceTagsState['byResource']>({
    key: 'onec.price-tags',
    slice: 'priceTags',
    lifetime: 'device',
    scope: 'user',
    version: 1,
    ttlMs: WEEK_MS,
    pick: (state) => select(state).byResource,
    decode: decodePriceTags,
    restore: (value) => priceTagsRestored(value),
  })
}
